import { useEffect, useState, useMemo } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  AlertCircle,
  Trophy,
  Star,
  Lock,
  Unlock,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import useAdminStore from '../../store/adminStore';
import { toast } from 'react-toastify';

const DIFFICULTY_OPTIONS = ['beginner', 'intermediate', 'advanced', 'expert'];

const ChallengeEditor = () => {
  const { challenges: rawChallenges, loading, fetchChallenges, createChallenge, updateChallenge, deleteChallenge } = useAdminStore();
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Extract challenge data from nested response structure
  // Backend returns: [{ challenge: {...}, submissions: X, completions: Y }, ...]
  const challenges = useMemo(() => {
    if (!rawChallenges || !Array.isArray(rawChallenges)) return [];
    return rawChallenges.map(item => {
      // Handle both nested and flat structures
      const challenge = item.challenge || item;
      return {
        ...challenge,
        submissions: item.submissions || 0,
        completions: item.completions || 0,
      };
    });
  }, [rawChallenges]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    difficulty: 'beginner',
    story_text: '',
    base_reward: 50,
    is_unlocked_by_default: false,
    position_x: 0,
    position_y: 0,
    estimated_time_minutes: 30,
    tags: [],
    requirements: {},
    score_criteria: {},
  });

  useEffect(() => {
    fetchChallenges();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      difficulty: 'beginner',
      story_text: '',
      base_reward: 50,
      is_unlocked_by_default: false,
      position_x: 0,
      position_y: 0,
      estimated_time_minutes: 30,
      tags: [],
      requirements: {},
      score_criteria: {},
    });
    setSelectedChallenge(null);
    setIsEditing(false);
    setIsCreating(false);
  };

  const handleEdit = (challenge) => {
    setSelectedChallenge(challenge);
    // Parse JSONB fields if they're strings
    let requirements = challenge.requirements;
    let scoreCriteria = challenge.score_criteria;
    try {
      if (typeof requirements === 'string') requirements = JSON.parse(requirements);
      if (typeof scoreCriteria === 'string') scoreCriteria = JSON.parse(scoreCriteria);
    } catch (e) {
      console.error('Error parsing JSON fields:', e);
    }

    setFormData({
      title: challenge.title || '',
      description: challenge.description || '',
      difficulty: challenge.difficulty || 'beginner',
      story_text: challenge.story_text || '',
      base_reward: challenge.base_reward || 50,
      is_unlocked_by_default: challenge.is_unlocked_by_default || false,
      position_x: challenge.position_x || 0,
      position_y: challenge.position_y || 0,
      estimated_time_minutes: challenge.estimated_time_minutes || 30,
      tags: challenge.tags || [],
      requirements: requirements || {},
      score_criteria: scoreCriteria || {},
    });
    setIsEditing(true);
    setIsCreating(false);
  };

  const handleCreate = () => {
    resetForm();
    setFormData(prev => ({
      ...prev,
      position_x: challenges.length,
      position_y: 0,
    }));
    setIsCreating(true);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (!formData.story_text.trim()) {
      toast.error('Story text is required');
      return;
    }

    // Prepare data with proper types for backend
    const submitData = {
      ...formData,
      // Ensure requirements and score_criteria are proper objects
      requirements: formData.requirements || {},
      score_criteria: formData.score_criteria || {},
      // Ensure base_reward is a number
      base_reward: parseInt(formData.base_reward) || 0,
      // Ensure position values are numbers (can be null for backend)
      position_x: formData.position_x ? parseInt(formData.position_x) : null,
      position_y: formData.position_y ? parseInt(formData.position_y) : null,
      estimated_time_minutes: formData.estimated_time_minutes ? parseInt(formData.estimated_time_minutes) : null,
    };

    setProcessing(true);
    try {
      if (isCreating) {
        await createChallenge(submitData);
        toast.success('Challenge created successfully');
      } else if (isEditing && selectedChallenge) {
        await updateChallenge(selectedChallenge.id, submitData);
        toast.success('Challenge updated successfully');
      }
      resetForm();
    } catch (err) {
      console.error('Save error:', err);
      toast.error(err.response?.data?.error || err.response?.data?.details || 'Failed to save challenge');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (challenge) => {
    if (!confirm(`Delete challenge "${challenge.title}"? This cannot be undone.`)) return;

    try {
      await deleteChallenge(challenge.id);
      toast.success('Challenge deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete challenge');
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-700';
      case 'intermediate': return 'bg-yellow-100 text-yellow-700';
      case 'advanced': return 'bg-orange-100 text-orange-700';
      case 'expert': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Challenge Editor</h2>
          <p className="text-gray-500">Create and manage system design challenges</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          New Challenge
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Challenge List */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800">Challenges ({challenges.length})</h3>
          </div>
          <div className="divide-y max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : challenges.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No challenges yet. Create your first one!
              </div>
            ) : (
              challenges.map((challenge) => (
                <div
                  key={challenge.id}
                  className={`${selectedChallenge?.id === challenge.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                >
                  <div className="px-6 py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Trophy className="w-5 h-5 text-yellow-500" />
                          <span className="font-medium text-gray-900">{challenge.title}</span>
                          {!challenge.is_unlocked_by_default && (
                            <Lock className="w-4 h-4 text-gray-400" title="Locked by default" />
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getDifficultyColor(challenge.difficulty)}`}>
                            {challenge.difficulty}
                          </span>
                          <span className="text-sm text-gray-500">
                            {challenge.base_reward} DP
                          </span>
                          {challenge.submissions > 0 && (
                            <span className="text-sm text-gray-400">
                              {challenge.submissions} submissions
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setExpandedId(expandedId === challenge.id ? null : challenge.id)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        >
                          {expandedId === challenge.id ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleEdit(challenge)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(challenge)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {expandedId === challenge.id && (
                      <div className="mt-3 pt-3 border-t text-sm text-gray-600">
                        <p>{challenge.description || challenge.story_text}</p>
                        {challenge.tags?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {challenge.tags.map((tag, i) => (
                              <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Editor Form */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">
              {isCreating ? 'New Challenge' : isEditing ? 'Edit Challenge' : 'Challenge Editor'}
            </h3>
            {(isEditing || isCreating) && (
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {!isEditing && !isCreating ? (
            <div className="p-8 text-center text-gray-500">
              <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Select a challenge to edit or create a new one</p>
            </div>
          ) : (
            <div className="p-6 space-y-4 max-h-[600px] overflow-y-auto">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Design a URL Shortener"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description of the challenge..."
                />
              </div>

              {/* Story Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Story Text</label>
                <textarea
                  value={formData.story_text}
                  onChange={(e) => setFormData({ ...formData, story_text: e.target.value })}
                  rows={4}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="The narrative context for this challenge..."
                />
              </div>

              {/* Difficulty & Base Reward */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  >
                    {DIFFICULTY_OPTIONS.map((d) => (
                      <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Base Reward (DP)</label>
                  <input
                    type="number"
                    value={formData.base_reward}
                    onChange={(e) => setFormData({ ...formData, base_reward: parseInt(e.target.value) || 0 })}
                    min="0"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Position & Time */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Position X</label>
                  <input
                    type="number"
                    value={formData.position_x}
                    onChange={(e) => setFormData({ ...formData, position_x: parseInt(e.target.value) || 0 })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Position Y</label>
                  <input
                    type="number"
                    value={formData.position_y}
                    onChange={(e) => setFormData({ ...formData, position_y: parseInt(e.target.value) || 0 })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Est. Time (min)</label>
                  <input
                    type="number"
                    value={formData.estimated_time_minutes}
                    onChange={(e) => setFormData({ ...formData, estimated_time_minutes: parseInt(e.target.value) || 30 })}
                    min="1"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Unlocked by Default */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Access</label>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_unlocked_by_default: !formData.is_unlocked_by_default })}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border ${
                    formData.is_unlocked_by_default
                      ? 'bg-green-50 border-green-200 text-green-700'
                      : 'bg-gray-50 border-gray-200 text-gray-700'
                  }`}
                >
                  {formData.is_unlocked_by_default ? (
                    <>
                      <Unlock className="w-4 h-4" />
                      Unlocked by Default
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      Requires Prerequisites
                    </>
                  )}
                </button>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={(formData.tags || []).join(', ')}
                  onChange={(e) => setFormData({
                    ...formData,
                    tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                  })}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., database, caching, scalability"
                />
              </div>

              {/* Requirements JSON */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Requirements (JSON)</label>
                <textarea
                  value={JSON.stringify(formData.requirements, null, 2)}
                  onChange={(e) => {
                    try {
                      setFormData({ ...formData, requirements: JSON.parse(e.target.value) });
                    } catch {
                      // Allow invalid JSON while typing
                    }
                  }}
                  rows={4}
                  className="w-full border rounded-lg px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder='{"min_qps": 1000, "max_latency_ms": 100}'
                />
              </div>

              {/* Score Criteria JSON */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Score Criteria (JSON)</label>
                <textarea
                  value={JSON.stringify(formData.score_criteria, null, 2)}
                  onChange={(e) => {
                    try {
                      setFormData({ ...formData, score_criteria: JSON.parse(e.target.value) });
                    } catch {
                      // Allow invalid JSON while typing
                    }
                  }}
                  rows={4}
                  className="w-full border rounded-lg px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder='{"performance": 30, "cost": 20, "reliability": 50}'
                />
              </div>

              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={processing}
                className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                {processing ? 'Saving...' : (isCreating ? 'Create Challenge' : 'Save Changes')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChallengeEditor;
